import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

interface UseUrlParamOptions {
  /** Replace the current history entry instead of pushing a new one (e.g. for typed input). */
  replace?: boolean;
}

interface SetUrlParamOptions extends UseUrlParamOptions {
  /** Other param keys to delete in the same URL update. */
  clear?: string[];
}

interface UseUrlParamsOptions extends UseUrlParamOptions {
  /** Treat the param as repeated (`?key=a&key=b`) and expose it as a string array. */
  multiple: true;
}

/** Binds a state value to a URL query param so back/forward navigation restores it. */
export function useUrlParam(
  key: string,
  options: UseUrlParamsOptions
): [string[], (value: string[], options?: SetUrlParamOptions) => void];
export function useUrlParam(
  key: string,
  options?: UseUrlParamOptions
): [string | undefined, (value: string | undefined, options?: SetUrlParamOptions) => void];
export function useUrlParam(
  key: string,
  { replace = false, ...rest }: UseUrlParamOptions & { multiple?: boolean } = {}
): [string | string[] | undefined, (value: never, options?: SetUrlParamOptions) => void] {
  const multiple = rest.multiple === true;
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.getAll(key).join('\n');

  const value = useMemo(
    () => (multiple ? (raw ? raw.split('\n') : []) : searchParams.get(key) || undefined),
    // `raw` captures every value for `key`, so searchParams itself need not be a dependency.
    [multiple, raw, key]
  );

  const setValue = useCallback(
    (next: string | string[] | undefined, options?: SetUrlParamOptions) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.delete(key);
          if (Array.isArray(next)) next.forEach((v) => params.append(key, v));
          else if (next) params.set(key, next);
          options?.clear?.forEach((k) => params.delete(k));
          return params;
        },
        { replace: options?.replace ?? replace }
      );
    },
    [key, replace, setSearchParams]
  );

  return [value, setValue];
}
